/**
 * 非遗数据Vuex模块
 * 管理非遗相关的数据和状态
 */

// 导入原有的数据结构
import { heritageMainData, heritageExtendedData } from '@/data/heritage-data.js'

const state = {
  // 主要数据
  mainData: heritageMainData,
  // 扩展数据
  extendedData: heritageExtendedData,
  // 搜索结果
  searchResults: [],
  // 搜索建议
  searchSuggestions: [
    '龙舟', '剪纸', '皮影', '英德红茶', '白沙茅龙笔', 
    '大吴泥塑', '雷州石狗', '象牙雕', '传统工艺', '非遗文化'
  ],
  // 当前选中的非遗项目
  currentItem: null,
  // 显示更多内容
  showMore: false
}

const mutations = {
  /**
   * 设置搜索结果
   * @param {Object} state - 状态对象
   * @param {Array} results - 搜索结果
   */
  SET_SEARCH_RESULTS(state, results) {
    state.searchResults = results
  },

  /**
   * 设置当前项目
   * @param {Object} state - 状态对象
   * @param {Object} item - 项目数据
   */
  SET_CURRENT_ITEM(state, item) {
    state.currentItem = item
  },

  /**
   * 切换显示更多状态
   * @param {Object} state - 状态对象
   * @param {boolean} show - 是否显示更多
   */
  SET_SHOW_MORE(state, show) {
    state.showMore = show
  },

  /**
   * 添加主要数据项
   * @param {Object} state - 状态对象
   * @param {Object} payload - 包含categoryId和itemData的对象
   */
  ADD_MAIN_ITEM(state, { categoryId, itemData }) {
    const category = state.mainData.find(cat => cat.id === categoryId)
    if (category) {
      category.items.push(itemData)
    }
  },

  /**
   * 添加扩展数据项
   * @param {Object} state - 状态对象
   * @param {Object} payload - 包含categoryId和itemData的对象
   */
  ADD_EXTENDED_ITEM(state, { categoryId, itemData }) {
    const category = state.extendedData.find(cat => cat.id === categoryId)
    if (category) {
      category.items.push(itemData)
    }
  }
}

const actions = {
  /**
   * 搜索非遗项目
   * @param {Object} context - Vuex context
   * @param {string} query - 搜索关键词
   */
  async searchHeritage({ commit, state }, query) {
    if (!query || query.trim() === '') {
      commit('SET_SEARCH_RESULTS', [])
      return []
    }

    const results = []
    const searchQuery = query.toLowerCase().trim()

    // 搜索主要数据
    state.mainData.forEach(category => {
      category.items.forEach(item => {
        const matchScore = calculateMatchScore(item, searchQuery)
        if (matchScore > 0) {
          results.push({
            ...item,
            category: category.categoryName,
            categoryClass: category.categoryClass,
            matchScore
          })
        }
      })
    })

    // 搜索扩展数据
    state.extendedData.forEach(category => {
      category.items.forEach(item => {
        const matchScore = calculateMatchScore(item, searchQuery)
        if (matchScore > 0) {
          results.push({
            ...item,
            category: category.categoryName,
            categoryClass: category.categoryClass,
            matchScore
          })
        }
      })
    })

    // 按匹配度排序
    results.sort((a, b) => b.matchScore - a.matchScore)

    commit('SET_SEARCH_RESULTS', results)
    return results
  },

  /**
   * 设置当前项目
   * @param {Object} context - Vuex context
   * @param {Object} item - 项目数据
   */
  setCurrentItem({ commit }, item) {
    commit('SET_CURRENT_ITEM', item)
  },

  /**
   * 切换显示更多
   * @param {Object} context - Vuex context
   * @param {boolean} show - 是否显示更多
   */
  toggleShowMore({ commit }, show) {
    commit('SET_SHOW_MORE', show)
  },

  /**
   * 根据ID查找主要数据项
   * @param {Object} context - Vuex context
   * @param {string} itemId - 项目ID
   */
  findMainItem({ state }, itemId) {
    for (const category of state.mainData) {
      const item = category.items.find(item => item.id === itemId)
      if (item) {
        return {
          ...item,
          category: category.categoryName,
          categoryClass: category.categoryClass
        }
      }
    }
    return null
  },

  /**
   * 根据ID查找扩展数据项
   * @param {Object} context - Vuex context
   * @param {string} itemId - 项目ID
   */
  findExtendedItem({ state }, itemId) {
    for (const category of state.extendedData) {
      const item = category.items.find(item => item.id === itemId)
      if (item) {
        return {
          ...item,
          category: category.categoryName,
          categoryClass: category.categoryClass
        }
      }
    }
    return null
  }
}

const getters = {
  /**
   * 获取主要数据
   * @param {Object} state - 状态对象
   * @returns {Array} 主要数据
   */
  getMainData: state => state.mainData,

  /**
   * 获取扩展数据
   * @param {Object} state - 状态对象
   * @returns {Array} 扩展数据
   */
  getExtendedData: state => state.extendedData,

  /**
   * 获取搜索结果
   * @param {Object} state - 状态对象
   * @returns {Array} 搜索结果
   */
  getSearchResults: state => state.searchResults,

  /**
   * 获取搜索建议
   * @param {Object} state - 状态对象
   * @returns {Array} 搜索建议
   */
  getSearchSuggestions: state => state.searchSuggestions,

  /**
   * 获取当前项目
   * @param {Object} state - 状态对象
   * @returns {Object} 当前项目
   */
  getCurrentItem: state => state.currentItem,

  /**
   * 获取显示更多状态
   * @param {Object} state - 状态对象
   * @returns {boolean} 是否显示更多
   */
  getShowMore: state => state.showMore
}

/**
 * 计算匹配度分数
 * @param {Object} item - 项目数据
 * @param {string} query - 搜索关键词
 * @returns {number} 匹配度分数
 */
function calculateMatchScore(item, query) {
  let score = 0

  // 标题完全匹配
  if (item.title.toLowerCase().includes(query)) {
    score += 10
  }

  // 描述匹配
  if (item.description && item.description.toLowerCase().includes(query)) {
    score += 5
  }

  // 标签匹配
  if (item.tags) {
    item.tags.forEach(tag => {
      if (tag.toLowerCase().includes(query)) {
        score += 3
      }
    })
  }

  // 关键词匹配
  if (item.keywords && item.keywords.toLowerCase().includes(query)) {
    score += 8
  }

  return score
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 